import React, { useCallback, useEffect, useState } from "react";
import { ukDay as fmt } from "@/lib/time";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { FileSearch, Loader2, Send, Check, X, Link2, Plus, Clock, MessageSquareWarning, Eye } from "lucide-react";
import { FileThumb } from "./FileThumb";
import { useLightbox, canPreview } from "./FileLightbox";

/** Each thing asked for: in (and what answered it), or still needed (and exactly what that is). */
function ItemRows({ r, testPrefix }) {
  const info = r.item_info || [];
  return (
    <ul className="mt-2.5 space-y-1.5" data-testid={`${testPrefix}-items-${r.id}`}>
      {(r.items || []).map((item, i) => {
        const it = info[i] || {};
        const done = it.done ?? r.items_done?.[i];
        return (
          <li key={item} className={`flex gap-2.5 rounded-lg border px-2.5 py-2 ${done ? "border-emerald-200 bg-emerald-50/60" : "border-slate-200 bg-slate-50/70"}`}
              data-testid={`${testPrefix}-item-${r.id}-${i}`}>
            <span className={`mt-0.5 grid place-items-center size-4 shrink-0 rounded-full ${done ? "bg-emerald-500 text-white" : "border-2 border-amber-400"}`}>
              {done && <Check className="size-2.5" strokeWidth={3.5} />}
            </span>
            <span className="min-w-0">
              <span className="block text-[12.5px] font-semibold text-slate-900">{item}</span>
              <span className={`block text-[11.5px] leading-snug ${done ? "text-emerald-800" : it.note ? "text-amber-800" : "text-slate-500"}`}
                    data-testid={`${testPrefix}-item-why-${r.id}-${i}`}>
                {done ? (it.why ? `In — matched by Connie: ${it.why}` : "In") : it.note ? `Still needed — ${it.note}` : `Still needed — ${it.how || "see the desk's note"}`}
              </span>
            </span>
          </li>
        );
      })}
    </ul>
  );
}
const PATHS = {
  admin: {
    list: (id) => `/hub/leads/${id}/doc-requests`,
    inbox: "/hub/doc-requests",
    create: (id) => `/hub/leads/${id}/doc-requests`,
    chase: (rid) => `/hub/doc-requests/${rid}/chase`,
    patch: (rid) => `/hub/doc-requests/${rid}`,
    items: "/hub/doc-items",
  },
  lender: {
    list: (id) => `/lender/leads/${id}/doc-requests`,
    create: (id) => `/lender/leads/${id}/doc-request`,
    chase: (rid) => `/lender/doc-requests/${rid}/chase`,
    patch: (rid) => `/lender/doc-requests/${rid}`,
    items: "/lender/doc-items",
  },
  referrer: {
    inbox: "/refer/doc-requests",
    create: (id) => `/refer/leads/${id}/doc-request`,
    chase: (rid) => `/refer/doc-requests/${rid}/chase`,
    items: "/refer/doc-items",
  },
};

const STATUS = {
  requested: { label: "Not asked yet", cls: "border-[#7da9ff]/45 bg-[#7da9ff]/[0.10] text-[#3B63A8]" },
  chased:    { label: "Client chased", cls: "border-[#0055FF]/25 bg-[#0055FF]/[0.06] text-[#0055FF]" },
  received:  { label: "Received",      cls: "border-teal-200 bg-teal-50 text-teal-700" },
  cancelled: { label: "Withdrawn",     cls: "border-slate-200 bg-slate-50 text-slate-500" },
};

/**
 * DocRequests — one missing document, requested once, chased through the right person.
 *
 * Case mode (`leadId` given): raise a request and chase on this case.
 * Inbox mode (no `leadId`): everything still outstanding across cases.
 */
export function DocRequests({ leadId, role = "admin", testPrefix = "docreq" }) {
  const P = PATHS[role];
  const [items, setItems] = useState([]);
  const [known, setKnown] = useState([]);
  const [open, setOpen] = useState(false);
  const [picked, setPicked] = useState([]);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(null);
  const [loading, setLoading] = useState(true);
  const { openOrDownload, lightbox } = useLightbox();
  const canCreate = !!leadId && !!P.create;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const path = leadId && P.list ? P.list(leadId) : P.inbox;
      const r = await api.get(path);
      let rows = r.data.items || [];
      if (leadId && !P.list) rows = rows.filter((x) => x.lead_id === leadId);
      setItems(rows);
      if (r.data.known_items) setKnown(r.data.known_items);
    } catch { setItems([]); }
    finally { setLoading(false); }
  }, [leadId, P]);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!open || known.length || !P.items) return;
    api.get(P.items).then((r) => setKnown(r.data.items || [])).catch(() => {});
  }, [open, known.length, P.items]);

  const create = async () => {
    if (!picked.length) { toast.error("Pick at least one document"); return; }
    setBusy("create");
    try {
      await api.post(P.create(leadId), { items: picked, note: note.trim() || null });
      toast.success("Requested — the desk and the introducer have been told");
      setPicked([]); setNote(""); setOpen(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not raise that request"); }
    finally { setBusy(null); }
  };

  const chase = async (r) => {
    setBusy(r.id);
    try {
      const res = await api.post(P.chase(r.id), {});
      const { sent_email, sent_sms } = res.data || {};
      toast.success(sent_email && sent_sms ? "Emailed and texted the client a private upload link"
        : sent_email ? "Emailed the client a private upload link"
        : sent_sms ? "Texted the client a private upload link"
        : "No email or mobile on file for this client — worth adding one");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not send that"); }
    finally { setBusy(null); }
  };

  const setStatus = async (r, status) => {
    setBusy(r.id);
    try { await api.patch(P.patch(r.id), { status }); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not update that"); }
    finally { setBusy(null); }
  };

  const copyLink = async (r) => {
    const url = `${window.location.origin}${r.upload_url}`;
    try { await navigator.clipboard.writeText(url); toast.success("Private upload link copied"); }
    catch { toast.error("Copy failed"); }
  };

  const chaseLabel = role === "referrer" ? "Chase my client" : "Chase the client";

  return (
    <div className="space-y-3" data-testid={`${testPrefix}-panel`}>
      {loading ? (
        <div className="grid place-items-center py-6 text-slate-400"><Loader2 className="size-4 animate-spin" /></div>
      ) : items.length === 0 ? (
        <div className="text-[12.5px] text-slate-500" data-testid={`${testPrefix}-empty`}>
          {leadId ? "Nothing outstanding on this case." : "Nothing outstanding — every document that's been asked for is in."}
        </div>
      ) : (
        <div className="space-y-2">
          {items.map((r) => {
            const st = STATUS[r.status] || STATUS.requested;
            const closed = r.status === "received" || r.status === "cancelled";
            return (
              <div key={r.id} className="rounded-xl border border-slate-200 bg-white p-3.5" data-testid={`${testPrefix}-row-${r.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    {r.case && (r.case.business_name || r.case.applicant_name) && (
                      <div className="text-[13px] font-semibold text-slate-900 truncate">
                        {r.case.business_name || r.case.applicant_name}
                        {r.case.reference && <span className="ml-1.5 font-mono text-[10.5px] text-slate-400">{r.case.reference}</span>}
                      </div>
                    )}
                    <div className="text-[11.5px] text-slate-500">
                      Asked by {r.requested_by_name || "the desk"} · {fmt(r.created_at)}
                      {r.chase_count > 0 && <> · chased {r.chase_count}×</>}
                    </div>
                    {!closed && r.waiting_days > 0 && (
                      <div className="mt-0.5 text-[11.5px] font-medium text-[#3B63A8]" data-testid={`${testPrefix}-waiting-${r.id}`}>
                        Waiting {r.waiting_days} day{r.waiting_days === 1 ? "" : "s"}
                      </div>
                    )}
                  </div>
                  <span className={`shrink-0 inline-flex items-center rounded-full border px-2 h-6 text-[10.5px] font-semibold ${st.cls}`}
                        data-testid={`${testPrefix}-status-${r.id}`}>
                    {st.label}
                  </span>
                </div>

                <ItemRows r={r} testPrefix={testPrefix} />

                {r.note && <div className="mt-2 text-[12px] text-slate-600 italic border-l-2 border-slate-200 pl-2">{r.note}</div>}

                {closed && r.closed_reason && (
                  <div className="mt-2 text-[11.5px] text-slate-500" data-testid={`${testPrefix}-closed-${r.id}`}>
                    Closed — {r.closed_reason}.
                  </div>
                )}

                {(r.files || []).length > 0 && (
                  <div className="mt-2.5 rounded-lg border border-teal-200 bg-teal-50/60 p-2.5">
                    <div className="text-[10.5px] uppercase tracking-[0.14em] font-semibold text-teal-700">On the case</div>
                    <div className="mt-1 space-y-1">
                      {r.files.map((f) => (
                        <button key={f.id} type="button"
                                onClick={() => openOrDownload({ id: f.id, filename: f.filename })}
                                className="w-full flex items-center gap-2 rounded-lg px-1 py-0.5 text-left hover:bg-teal-100/60 transition-colors"
                                data-testid={`${testPrefix}-file-${f.id}`}>
                          <FileThumb fileId={f.id} alt={f.filename} size={32} hasThumb={f.has_thumb} />
                          <span className="flex-1 text-[12px] text-teal-900 truncate">{f.filename} · {fmt(f.uploaded_at)}</span>
                          {canPreview(f.filename) && <Eye className="size-3 text-teal-600 shrink-0" />}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {!closed && (
                  <div className="mt-3 flex items-center gap-1.5 flex-wrap">
                    <Button size="sm" onClick={() => chase(r)} disabled={busy === r.id}
                            className="rounded-full h-8 bg-[#0055FF] hover:bg-[#0044CC] text-[12px] font-semibold"
                            data-testid={`${testPrefix}-chase-${r.id}`}>
                      {busy === r.id ? <Loader2 className="size-3.5 animate-spin" /> : <><Send className="size-3 mr-1.5" /> {r.chase_count ? "Chase again" : chaseLabel}</>}
                    </Button>
                    {r.upload_url && (
                      <Button size="sm" variant="outline" onClick={() => copyLink(r)} className="rounded-full h-8 text-[12px]"
                              data-testid={`${testPrefix}-copy-${r.id}`}>
                        <Link2 className="size-3 mr-1.5" /> Copy link
                      </Button>
                    )}
                    {P.patch && (role !== "lender" || r.mine) && (
                      <>
                        <Button size="sm" variant="outline" onClick={() => setStatus(r, "received")} disabled={busy === r.id}
                                className="rounded-full h-8 text-[12px]" data-testid={`${testPrefix}-received-${r.id}`}>
                          <Check className="size-3 mr-1.5" /> Got it another way
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => setStatus(r, "cancelled")} disabled={busy === r.id}
                                className="rounded-full h-8 text-[12px] text-slate-500" data-testid={`${testPrefix}-cancel-${r.id}`}>
                          <X className="size-3 mr-1.5" /> No longer needed
                        </Button>
                      </>
                    )}
                  </div>
                )}
                {!closed && r.chase_count === 0 && (
                  <div className="mt-2 text-[11px] text-slate-400 inline-flex items-center gap-1">
                    <Clock className="size-3" /> The client hasn't been contacted yet — the chase sends them a private upload link.
                  </div>
                )}
                {!closed && role === "lender" && P.patch && r.mine && (
                  <div className="mt-1.5 text-[11px] text-slate-400">
                    Close it the moment you don't need it and the chasing stops immediately.
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {canCreate && (open ? (
        <div className="rounded-xl border border-slate-200 bg-slate-50/60 p-3.5 space-y-3" data-testid={`${testPrefix}-form`}>
          <div className="text-[10.5px] uppercase tracking-[0.18em] font-semibold text-slate-600">What's missing?</div>
          <div className="flex flex-wrap gap-1.5">
            {known.map((i) => {
              const on = picked.includes(i);
              return (
                <button key={i} type="button"
                        onClick={() => setPicked((p) => on ? p.filter((x) => x !== i) : p.length >= 8 ? p : [...p, i])}
                        className={`rounded-full border px-3 h-8 text-[12px] font-medium transition-colors ${
                          on ? "border-[#0055FF] bg-[#0055FF] text-white" : "border-slate-200 bg-white text-slate-700 hover:border-slate-300"}`}
                        data-testid={`${testPrefix}-item-${i.replace(/[^a-zA-Z0-9]+/g, "-").toLowerCase()}`}>
                  {i}
                </button>
              );
            })}
          </div>
          <Textarea value={note} onChange={(e) => setNote(e.target.value)} className="min-h-[70px] bg-white"
                    placeholder="Anything specific? e.g. we only need the trading account, not the savings one"
                    data-testid={`${testPrefix}-note`} />
          <div className="flex items-center justify-end gap-2">
            <Button variant="ghost" size="sm" onClick={() => { setOpen(false); setPicked([]); }} className="rounded-full text-slate-500">Cancel</Button>
            <Button size="sm" onClick={create} disabled={busy === "create" || !picked.length}
                    className="rounded-full bg-slate-900 hover:bg-slate-800 font-semibold" data-testid={`${testPrefix}-submit`}>
              {busy === "create" ? <Loader2 className="size-3.5 animate-spin" /> : `Request ${picked.length || ""} document${picked.length === 1 ? "" : "s"}`}
            </Button>
          </div>
          <div className="text-[11px] text-slate-400 inline-flex items-start gap-1.5">
            <MessageSquareWarning className="size-3 mt-0.5 shrink-0" />
            {role === "lender"
              ? "The Connexis desk and the introducer are told straight away. You can chase the client yourself from here — the message goes out under Connexis with a private upload link."
              : "Everyone on the case is told. The client gets a private upload link by text and email — a phone photo of each document is fine."}
          </div>
        </div>
      ) : (
        <Button variant="outline" size="sm" onClick={() => setOpen(true)} className="rounded-full h-9 text-[12.5px]"
                data-testid={`${testPrefix}-open`}>
          <Plus className="size-3.5 mr-1.5" /> Ask for a document
        </Button>
      ))}
      {lightbox}
    </div>
  );
}

/** Card wrapper for portal pages that want this as a standalone panel. */
export function DocRequestsCard({ role, title = "Documents we're waiting on", subtitle }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="doc-requests-card">
      <div className="flex items-center gap-2.5">
        <span className="grid place-items-center size-10 rounded-xl border border-slate-200 bg-slate-50 text-slate-600">
          <FileSearch className="size-4.5" />
        </span>
        <div>
          <div className="text-[15px] font-semibold text-slate-900">{title}</div>
          <div className="text-[12px] text-slate-500">
            {subtitle || "Send your client a private link and the file lands straight on the case."}
          </div>
        </div>
      </div>
      <div className="mt-4"><DocRequests role={role} testPrefix="docreq-inbox" /></div>
    </div>
  );
}
